import { useState, useMemo } from 'react';
import { Stack, Drawer, Group, Text, Anchor, Divider } from '@mantine/core';
import { isMobile } from 'react-device-detect';
import { HiArrowLeft } from '@react-icons/all-files/hi/HiArrowLeft';
import { IoMdOpen } from '@react-icons/all-files/io/IoMdOpen';
import { useUniversalWorkerData } from '@company/hooks/useUniversalWorkerData';
import ReviewSidePopUp from '@company/components/ReviewSidePopup/ReviewSidePopup';
import { useDirectMessageContext } from '@company/state/directMessageContext';
import DirectMessageChat from '@components/Chat/DirectMessageChat';
import { SideEndModal } from '@components/elements';
import {
  getWorkerProfileUrl,
  getDrawerProps,
  getDrawerSize,
} from '@company/utils/worker-modal-helper';
import { IWorkerJobData } from '@th-types/workerProfile.type';
import {
  WorkerModalProps,
  WorkerModalTabsEnum,
} from '@company/types/worker-modal.type';
import WorkerModalTabsComponent from './WorkerModalTabs';
import WorkerInfoSection from './WorkerInfoSection';

export default function WorkerModal({
  jobAssignmentId,
  workerId,
  jobId,
  onClose,
  show = true,
  showJobDetails = true,
  initialTab = WorkerModalTabsEnum.THIS_JOB,
  worker: preloadedWorker,
  zIndex,
}: WorkerModalProps) {
  const [showReview, setShowReview] = useState(false);
  const { worker: directMessageWorker, clearDirectMessageData } =
    useDirectMessageContext();
  const {
    data: workerData,
    isLoading,
    error,
    refetch,
  } = useUniversalWorkerData({
    jobAssignmentId,
    workerId,
    jobId,
    enabled: !preloadedWorker, // Don't fetch if we have preloaded data
  });

  // Use preloaded worker data if available, otherwise use fetched data
  const finalWorkerData =
    workerData ??
    (preloadedWorker
      ? ({ worker: preloadedWorker } as IWorkerJobData)
      : undefined);

  const closeWorkerProfile = () => onClose();

  const drawerProps = useMemo(() => getDrawerProps(show), [show]);

  // Render review modal if showReview is true and we have job assignment data
  if (showReview && finalWorkerData?.jobAssignment) {
    return (
      <Drawer
        {...drawerProps}
        zIndex={zIndex}
        onClose={closeWorkerProfile}
        radius="sm"
        size={getDrawerSize(isMobile)}
      >
        <ReviewSidePopUp
          jobAssignment={finalWorkerData.jobAssignment}
          workerInfo={finalWorkerData.worker}
          onSubmitReview={() => {
            setShowReview(false);
            refetch(); // Refresh data after review submission
          }}
          onCloseReviewForm={() => setShowReview(false)}
        />
      </Drawer>
    );
  }

  // Render worker profile modal (always show when opened)
  const renderWorkerProfileModal = show && (
    <Drawer
      {...drawerProps}
      zIndex={zIndex}
      onClose={closeWorkerProfile}
      radius="sm"
      size={getDrawerSize(isMobile)}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group
            gap="xs"
            style={{ cursor: 'pointer' }}
            onClick={closeWorkerProfile}
          >
            <HiArrowLeft size={20} />
            <Text fw={700}>Back</Text>
          </Group>
          {finalWorkerData?.worker && (
            <Anchor
              href={getWorkerProfileUrl(finalWorkerData.worker)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <IoMdOpen size={20} />
              {!isMobile && <Text fw={700}>Open profile in new window</Text>}
            </Anchor>
          )}
        </Group>

        <Divider />

        {/* Worker Info Section */}
        {finalWorkerData?.worker && (
          <WorkerInfoSection
            workerData={finalWorkerData}
            onOpenReviewModal={() => setShowReview(true)}
            jobId={jobId}
            showJobDetails={showJobDetails}
          />
        )}

        <WorkerModalTabsComponent
          workerData={finalWorkerData}
          isLoading={isLoading}
          error={error}
          initialTab={
            showJobDetails && jobAssignmentId
              ? WorkerModalTabsEnum.THIS_JOB
              : initialTab
          }
          showJobDetails={showJobDetails}
          jobId={jobId}
        />
      </Stack>
    </Drawer>
  );

  // Render direct message modal when direct message is active AND worker modal is showing
  const renderDirectMessageModal = directMessageWorker && show && (
    <SideEndModal
      show={!!directMessageWorker}
      width={isMobile ? '90%' : '50%'}
      onHide={clearDirectMessageData}
    >
      <DirectMessageChat
        worker={directMessageWorker}
        onClose={clearDirectMessageData}
      />
    </SideEndModal>
  );

  return (
    <>
      {renderWorkerProfileModal}
      {renderDirectMessageModal}
    </>
  );
}
